<template>
  <div class="flex items-center gap-2 flex-wrap">
    <!-- Active Status Filter -->
    <Select :model-value="activeFilterValue" @update:model-value="handleActiveFilter">
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="activeFilterValue !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <CircleDot class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Statuses</SelectItem>
        <SelectItem value="active">Active</SelectItem>
        <SelectItem value="inactive">Inactive</SelectItem>
      </SelectContent>
    </Select>

    <!-- Reachability Filter -->
    <Select :model-value="reachabilityFilterValue" @update:model-value="handleReachabilityFilter">
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="reachabilityFilterValue !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Signal class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Reachability" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Reachability Levels</SelectItem>
        <SelectItem value="EASY">Easy</SelectItem>
        <SelectItem value="HARD">Hard</SelectItem>
      </SelectContent>
    </Select>

    <!-- Relationship Type Filter -->
    <Select :model-value="relationshipFilterValue" @update:model-value="handleRelationshipFilter">
      <SelectTrigger
        class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5"
        :class="relationshipFilterValue !== 'all'
          ? 'border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple'
          : 'border-gray-200 text-gray-600'"
      >
        <Users class="w-3 h-3 shrink-0" />
        <SelectValue placeholder="Relationship" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Types</SelectItem>
        <SelectItem value="NON_GRAD_CHAMPION">Non-Grad Champion</SelectItem>
        <SelectItem value="GRAD">Grad</SelectItem>
        <SelectItem value="RECRUITER_MANAGER">Recruiting Manager</SelectItem>
      </SelectContent>
    </Select>

    <!-- Clear all -->
    <button
      v-if="activeFilterValue !== 'all' || reachabilityFilterValue !== 'all' || relationshipFilterValue !== 'all'"
      @click="clearAllFilters"
      class="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-full border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-500 transition-colors"
    >
      <X class="w-3 h-3" />
      Clear all
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useReferrersStore } from '@/stores/referrers'
import { CircleDot, Signal, Users, X } from 'lucide-vue-next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const router = useRouter()
const referrersStore = useReferrersStore()

const searchQuery = ref('')



// Computed values for filter dropdowns
const activeFilterValue = computed(() => {
  if (referrersStore.filterActive === null) return 'all'
  return referrersStore.filterActive ? 'active' : 'inactive'
})

const reachabilityFilterValue = computed(() => {
  return referrersStore.filterReachability ?? 'all'
})

const relationshipFilterValue = computed(() => {
  return referrersStore.filterRelationshipType ?? 'all'
})

// Filter handlers
const handleActiveFilter = (value) => {
  let filterValue = null
  if (value === 'active') filterValue = true
  else if (value === 'inactive') filterValue = false
  
  referrersStore.setActiveFilter(filterValue, router)
}

const handleReachabilityFilter = (value) => {
  const filterValue = value === 'all' ? null : value
  referrersStore.setReachabilityFilter(filterValue, router)
}

const handleRelationshipFilter = (value) => {
  const filterValue = value === 'all' ? null : value
  referrersStore.setRelationshipTypeFilter(filterValue, router)
}

// Clear all filters function
const clearAllFilters = () => {
  referrersStore.clearFilters(router)
  searchQuery.value = ''
}

// Initialize search query from store when component loads
onMounted(() => {
  searchQuery.value = referrersStore.searchQuery
})
</script> 